import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Landmark, ShieldCheck, TimerOff } from "lucide-react";

import OverviewKpiCard from "../companyOverview/OverviewKpiCard";
import { formatDateOnly } from "../../../utils/datetime";
import { chf, chfPrecise, countText, percent } from "./financeFormat";
import {
  selectDueOrOverdue,
  selectLandlordDeposits,
  selectLandlordDepositQuality,
  selectLandlordDepositsAsOf,
  selectLandlordPremiumContext,
  selectLandlordSecurityTypeRows,
  selectOpenInvoiceCount,
  selectOpenReceivables,
  selectReceivablesAsOf,
  selectTenantCashDeposits,
} from "./financeSelectors";

/**
 * The four figures that answer "right now" rather than "in the selected month".
 *
 * WHY THEY NEED THEIR OWN SECTION
 * -------------------------------
 * Placed under a heading reading "Zeitraum · August 2026", a current-state
 * figure silently claims to be August's. None of these four is: receivables are
 * open across ALL rent months, and both deposit figures are balances with no
 * period at all. The separate heading above this grid is the correction, and
 * every card repeats its own as-of date so the claim survives being read alone.
 *
 * FOUR DATES, NOT ONE SNAPSHOT
 * ----------------------------
 * The receivables block and the landlord deposit answer carry their OWN
 * `as_of_date` / `as_of`, computed by different services, and each card shows
 * the one it actually has. The tenant deposit snapshot arrives as a prop from
 * the page and is shown without a date rather than borrowing a neighbour's:
 * stamping one card with another card's timestamp would be a fabricated claim
 * about when it was true. No date is ever read off the device clock.
 *
 * "KAUTION" MEANS TWO OPPOSITE THINGS HERE
 * ----------------------------------------
 * "Erhaltene Barkautionen" is money TENANTS placed with us — third-party money,
 * a liability. "Sicherheitsleistungen bei Vermietern" is security WE provided to
 * landlords — the other side of the balance. They share a German word and
 * nothing else, they sit side by side under different names on purpose, and they
 * are never added together. Neither is revenue and neither is a cost.
 *
 * AND THE SECOND ONE IS NOT CASH (K1)
 * -----------------------------------
 * That card used to be titled "Gebundenes Kapital", which asserts the money is
 * tied up. Production disproves it: every insured unit records
 * `landlord_deposit_amount` as the GUARANTEED sum, while what actually leaves
 * the account is the annual premium — so the whole headline was reading as
 * blocked liquidity that is not blocked. The title now names the thing that IS
 * true (securities were provided) and the breakdown underneath says in which
 * form, which is the part that decides whether any cash is involved at all.
 */

export default function FinanceCurrentStateGrid({
  revenueAnalytics,
  landlordDeposits,
  cashDeposits,
  cashDepositsLoading = false,
  onOpenCashDepositDetails,
  loading = false,
  landlordLoading = false,
  onRetry,
  onRetryLandlord,
}) {
  const ra = revenueAnalytics?.data;
  const open = selectOpenReceivables(ra);
  const openCount = selectOpenInvoiceCount(ra);
  // The backend roll-up, used as published. `due_current` is `due_today` plus
  // every overdue bucket, so the label says "Fällig & überfällig" — an invoice
  // due today is due, and calling it overdue would be false. See
  // `selectDueOrOverdue` for the service reference.
  const dueOrOverdue = selectDueOrOverdue(ra);
  const receivablesAsOf = selectReceivablesAsOf(ra);

  const landlord = landlordDeposits?.data;
  const securities = selectLandlordDeposits(landlord);
  const securityRows = selectLandlordSecurityTypeRows(landlord);
  const premium = selectLandlordPremiumContext(landlord);
  const premiumGaps = selectLandlordDepositQuality(landlord);
  const landlordAsOf = selectLandlordDepositsAsOf(landlord);

  const tenantHeld = selectTenantCashDeposits(cashDeposits);

  const asOfText = (value) => (value ? `Stand ${formatDateOnly(value)}` : "Aktueller Stand");

  const openSubtext = [
    openCount === null ? null : `${countText(openCount)} offene Rechnungen`,
    asOfText(receivablesAsOf),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-3">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <OverviewKpiCard
        testId="finance-open-receivables"
        title="Offene Forderungen"
        value={chf(open)}
        subtext={openSubtext}
        icon={<Clock className="h-4 w-4" />}
        accent="rose"
        compact
        loading={loading}
        error={revenueAnalytics?.error}
        onRetry={onRetry}
      />

      <OverviewKpiCard
        testId="finance-due-or-overdue"
        title="Fällig & überfällig"
        value={chf(dueOrOverdue)}
        subtext={`Fälligkeit erreicht, heute eingeschlossen · ${asOfText(receivablesAsOf)}`}
        icon={<TimerOff className="h-4 w-4" />}
        accent="amber"
        compact
        loading={loading}
        error={revenueAnalytics?.error}
        onRetry={onRetry}
      />

      <OverviewKpiCard
        testId="finance-tenant-deposits"
        title="Erhaltene Barkautionen"
        value={chf(tenantHeld)}
        subtext="Von Mietern hinterlegt · Aktueller Stand"
        icon={<ShieldCheck className="h-4 w-4" />}
        accent="teal"
        compact
        loading={cashDepositsLoading}
        footer={
          /* The page owns this modal and loads its details; the tab is handed
             the opener rather than building a second one. */
          onOpenCashDepositDetails ? (
            <button
              type="button"
              onClick={onOpenCashDepositDetails}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Details anzeigen
            </button>
          ) : null
        }
      />

      <OverviewKpiCard
        testId="finance-landlord-deposits"
        title="Sicherheitsleistungen bei Vermietern"
        value={chf(securities)}
        /* The title already says "bei Vermietern"; repeating it here would cost
           a line and add nothing. What the subtext still owes the reader is the
           day this was true. */
        subtext={asOfText(landlordAsOf)}
        icon={<Landmark className="h-4 w-4" />}
        accent="violet"
        compact
        loading={landlordLoading}
        error={landlordDeposits?.error}
        onRetry={onRetryLandlord}
        footer={
          securityRows.length > 0 || premium.present || premiumGaps.present ? (
            <div className="space-y-1">
              {securityRows.length > 0 ? (
                /* In WHICH FORM the securities were provided. This is the line
                   that separates an insurance guarantee from real blocked cash,
                   and it is the reason the headline may no longer be called
                   "Gebundenes Kapital". Only forms actually carrying an amount
                   appear; a zero bucket is not a fact worth a row.

                   These rows need not add up to the headline and nothing is
                   invented so that they do — see `selectLandlordSecurityTypeRows`. */
                <dl
                  data-testid="finance-landlord-security-breakdown"
                  className="m-0 space-y-0.5"
                >
                  {securityRows.map((row) => (
                    <div
                      key={row.type}
                      data-testid={`finance-landlord-security-${row.type}`}
                      className="flex flex-wrap items-baseline justify-between gap-x-2"
                    >
                      <dt className="text-[11px] text-muted-foreground">{row.label}</dt>
                      <dd className="m-0 text-[11px] text-card-foreground">
                        {chf(row.amount)}
                        {row.share === null ? null : (
                          <span className="ml-1 text-muted-foreground">
                            · {percent(row.share)}
                          </span>
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {premium.present ? (
                /* An ANNUAL figure, and it says so. Its monthly prorated form
                   is already inside `operating_costs.total_chf` as
                   `deposit_insurance_chf`; printing it here without "p.a."
                   would read as this month's cost and invite the double count
                   the contract exists to prevent. */
                <p
                  data-testid="finance-landlord-premium"
                  className="m-0 text-[11px] leading-snug text-muted-foreground"
                >
                  {/* To the centime, unlike every other figure on this card.
                      The premium is a real recurring cost an operator
                      reconciles against an invoice, and it is not a summand of
                      the headline — so rounding it buys no visual consistency
                      and loses real Rappen. */}
                  Versicherungsprämien p.a.: {chfPrecise(premium.total)}
                </p>
              ) : null}
              {premiumGaps.present ? (
                /* Not an alarm and not a zero: a missing premium means the cost
                   is UNKNOWN, so the count is stated and nothing is imputed. */
                <p
                  data-testid="finance-landlord-premium-gap"
                  className="m-0 text-[11px] leading-snug text-muted-foreground"
                >
                  {countText(premiumGaps.count)} versicherte Kaution(en) ohne
                  hinterlegte Jahresprämie
                </p>
              ) : null}
            </div>
          ) : null
        }
      />
    </div>

    {/* One CTA for both deposit cards, not one per card: the two lead to the
        same page, and a button on each would be two doors to one room. The
        Barkautionen card keeps its own "Details anzeigen" modal — that answers
        a narrower question and is not replaced here. */}
    <div className="flex justify-end">
      <Link
        data-testid="finance-deposits-cta"
        to="/admin/kautionen"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
      >
        Kautionen &amp; Sicherheiten anzeigen
        <ArrowRight aria-hidden className="h-3.5 w-3.5" />
      </Link>
    </div>
    </div>
  );
}
